import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Popconfirm,
  Tag,
  Space,
  Input,
  Typography,
  Descriptions,
  Divider,
  Collapse,
  Image,
  message,
} from 'antd';
import { useOrder } from '../../store/orderContext';
import { Order } from '../../types/order';
import { updateOrderStatus } from '../../api/orderApi';
import { useTranslation } from 'react-i18next';
import PrintInvoiceButton from '../Invoice/PrintInvoiceButton';
import { useAuth } from '../../store/AuthContext';

const { Link, Text } = Typography;
const { Panel } = Collapse;

// === Highlight Helper ===
function highlightText(text: any, search: string) {
  if (!search || typeof text !== 'string') return text;
  const regex = new RegExp(`(${search})`, 'gi');
  return (
    <span>
      {text.split(regex).map((part: string, i: number) =>
        part.toLowerCase() === search.toLowerCase() ? (
          <mark key={i} style={{ background: '#FFEB3B', padding: 0 }}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
}

interface Props {
  onEdit: (order: Order) => void;
}

const OrderList: React.FC<Props> = ({ onEdit }) => {
  const { orders, deleteOrder, fetchOrdersByStatus, downloadOrdersPdf } = useOrder();
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const { hasPermission } = useAuth();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const statusFlow = ['processing', 'ordered', 'shipped', 'inStore', 'delivered'];

  const getNextStatus = (current: string) => {
    const index = statusFlow.indexOf(current);
    return index < statusFlow.length - 1 ? statusFlow[index + 1] : null;
  };

  const getPreviousStatus = (current: string) => {
    const index = statusFlow.indexOf(current);
    return index > 0 ? statusFlow[index - 1] : null;
  };

  const handleChangeStatus = async (order: Order) => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return message.info(t('already_last_status'));
    try {
      await updateOrderStatus(order.id.toString(), nextStatus);
      message.success(t('status_updated', { status: t(nextStatus) }));
      fetchOrdersByStatus(order.status);
    } catch (error) {
      message.error(t('status_update_failed'));
    }
  };

  const handleBackStatus = async (order: Order) => {
    const previousStatus = getPreviousStatus(order.status);
    if (!previousStatus) return message.info(t('already_first_status'));
    try {
      await updateOrderStatus(order.id.toString(), previousStatus);
      message.success(t('status_reverted', { status: t(previousStatus) }));
      fetchOrdersByStatus(order.status);
    } catch (error) {
      message.error(t('status_revert_failed'));
    }
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const downloadImage = (url: string, filename?: string) => {
    if (isMobile) {
      window.open(url, '_blank');
      return;
    }
    let ext = url.split('.').pop()?.split('?')[0] || 'jpg';
    let defaultName = 'image.' + ext;
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || defaultName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
      })
      .catch(() => {
        message.error('Failed to download image');
      });
  };

  // === Search Filter Logic (improved) ===
  const filteredOrders = useMemo(() => {
    const searchTerm = search.toLowerCase();
    if (!searchTerm) return orders;
    return orders.filter(order => {
      const match = (val?: string | number) =>
        (val ?? '').toString().toLowerCase().includes(searchTerm);

      // Search in order top-level fields
      if (
        match(order.customerCode) ||
        match(order.status) ||
        match(order.cardLink) ||
        match(order.totalPrice) ||
        match(order.pay) ||
        match(order.remain) ||
        match(order.invoiceNumber)
      ) return true;

      // Search in items
      if (order.items?.some(item =>
        match(item.note) ||
        match(item.qty) ||
        match(item.image) ||
        match(item?.customization?.note)
      )) return true;

      // Search in subOrders
      if (order.subOrders?.some(sub =>
        match(sub.link) ||
        match(sub.pieces) ||
        match(sub.note)
      )) return true;

      // Search in orderDetails
      if (order.orderDetails?.some(detail =>
        match(detail.orderNo) ||
        match(detail.orderTrackNo) ||
        match(detail.accountNo)
      )) return true;

      return false;
    });
  }, [orders, search]);

  // === Expanded Row Render (highlighted) ===
  const expandedRowRender = (order: Order) => (
    <>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label={t('customerCode')}>
          {highlightText(order.customerCode ?? '', search)}
        </Descriptions.Item>
        <Descriptions.Item label={t('card_link')}>
          <Link href={order.cardLink} target="_blank">{highlightText(order.cardLink ?? '', search)}</Link>
        </Descriptions.Item>
        <Descriptions.Item label={t('total_price')}>
          {highlightText((order.totalPrice ?? '').toString(), search)}
        </Descriptions.Item>
        <Descriptions.Item label={t('pay')}>
          {highlightText((order.pay ?? '').toString(), search)}
        </Descriptions.Item>
        <Descriptions.Item label={t('remain')}>
          {highlightText((order.remain ?? '').toString(), search)}
        </Descriptions.Item>
        <Descriptions.Item label={t('status')}>
          <Tag color="blue">{highlightText(t(order.status), search)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('created_at')}>
          {order.createdAt ? highlightText(new Date(order.createdAt).toLocaleString(), search) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('updated_at')}>
          {order.updatedAt ? highlightText(new Date(order.updatedAt).toLocaleString(), search) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('delivery_cost')}>
          {highlightText((order.deliveryCost ?? '—').toString(), search)}
        </Descriptions.Item>
        <Descriptions.Item label={t('invoice_number')}>
          {highlightText(order.invoiceNumber ?? '—', search)}
        </Descriptions.Item>
        <Descriptions.Item label={t('qty')}>
          {highlightText((order.qty ?? '—').toString(), search)}
        </Descriptions.Item>
      </Descriptions>
      <Divider orientation="left" style={{ marginTop: 24 }}>{t('order_items')}</Divider>
      <Collapse ghost>
        {order.items?.map((item, index) => (
          <Panel header={`${t('item')} ${index + 1}`} key={index}>
            <Space direction="vertical" size="small">
              {item.image && (
                <>
                  <Image width={100} src={item.image} preview={{ src: item.image }} />
                  <Button
                    size="small"
                    style={{ marginTop: 8 }}
                    onClick={() => downloadImage(item.image, `order_item_${index + 1}.jpg`)}
                  >
                    {t('download_image') || 'Download Image'}
                  </Button>
                </>
              )}
              <Text><strong>{t('quantity')}:</strong> {highlightText((item.qty ?? '').toString(), search)}</Text>
              <Text><strong>{t('note')}:</strong> {highlightText(item.note ?? '—', search)}</Text>
              <Text><strong>{t('detail')}:</strong> {highlightText(item?.note ?? '—', search)}</Text>
              <Text><strong>{t('customization_note')}:</strong> {highlightText(item.customization?.note ?? '—', search)}</Text>
              <Text><strong>{t('customization_detail')}:</strong> {highlightText(item.customization?.detail ?? '—', search)}</Text>
            </Space>
          </Panel>
        ))}
      </Collapse>
      <Divider orientation="left" style={{ marginTop: 24 }}>{t('sub_orders')}</Divider>
      <Collapse ghost>
        {order.subOrders?.map((sub, index) => (
          <Panel header={`${t('sub_order')} ${index + 1}`} key={`sub-${index}`}>
            <Text>
              <strong>{t('link')}:</strong> <Link href={sub.link} target="_blank">{highlightText(sub.link ?? '', search)}</Link>
            </Text><br />
            <Text><strong>{t('pieces')}:</strong> {highlightText((sub.pieces ?? '').toString(), search)}</Text><br />
            <Text><strong>{t('note')}:</strong> {highlightText(sub.note ?? '—', search)}</Text>
          </Panel>
        ))}
      </Collapse>
      <Divider orientation="left" style={{ marginTop: 24 }}>{t('order_details')}</Divider>
      <Collapse ghost>
        {order.orderDetails?.map((detail, index) => (
          <Panel header={`${t('order_detail')} ${index + 1}`} key={`detail-${index}`}>
            <Text><strong>{t('order_no')}:</strong> {highlightText(detail.orderNo ?? '', search)}</Text><br />
            <Text><strong>{t('tracking_no')}:</strong> {highlightText(detail.orderTrackNo ?? '', search)}</Text><br />
            <Text><strong>{t('account_no')}:</strong> {highlightText(detail.accountNo ?? '—', search)}</Text>
          </Panel>
        ))}
      </Collapse>
    </>
  );

  // === Table Columns (highlighted) ===
  const columns = [
    {
      title: t('customer'),
      dataIndex: 'customerCode',
      render: (text: string) => highlightText(text, search),
    },
    {
      title: t('card_link'),
      dataIndex: 'cardLink',
      render: (link: string) => (
        <Link href={link} target="_blank">
          {highlightText(t('open_link'), search)}
        </Link>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      render: (status: string) => <Tag color="blue">{highlightText(t(status), search)}</Tag>,
    },
    {
      title: t('actions'),
      render: (_: any, record: Order) => (
        <div
          style={{
            display: 'flex',
            justifyContent: i18n.language === 'ar' || i18n.language === 'ckb' ? 'flex-end' : 'flex-start',
          }}
        >
          <Space>
            {hasPermission('order.update') && (
              <Button type="primary" size="small" onClick={() => onEdit(record)}>
                {t('edit')}
              </Button>
            )}
            {hasPermission('order.nextStatus') ? (
              <Button size="small" onClick={() => handleChangeStatus(record)}>
                ➡ {t('next')}
              </Button>
            ) : null}
            {hasPermission('order.prevStatus') ? (
              <Button size="small" danger onClick={() => handleBackStatus(record)}>
                ⬅ {t('back')}
              </Button>
            ) : null}
            {hasPermission('order.delete') ? (
              <Popconfirm title={t('sure_to_delete')} onConfirm={() => deleteOrder(record.id)}>
                <Button size="small" type="primary" danger>
                  {t('delete')}
                </Button>
              </Popconfirm>
            ) : null}
            <PrintInvoiceButton order={record} />
          </Space>
        </div>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], selectedRows: Order[]) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedOrders(selectedRows);
    },
  };

  // === PDF Download Button ===
  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadOrdersPdf(selectedOrders.map(o => o.id), i18n.language);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'orders.pdf';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);
      message.success(t('download_success') || 'PDF downloaded!');
    } catch (e) {
      message.error(t('download_failed') || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div
      style={{
        overflowX: 'auto',
        direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr',
      }}
    >
      <Button
        type="primary"
        loading={pdfLoading}
        disabled={selectedOrders.length === 0}
        style={{ marginBottom: 16, marginRight: 12 }}
        onClick={handleDownloadPdf}
      >
        {t('download_pdf') || 'Download PDF'}
      </Button>
      <Input.Search
        allowClear
        style={{ marginBottom: 16, maxWidth: 350 }}
        placeholder={t('search_orders') as string}
        onChange={(e) => setSearch(e.target.value)}
        value={search}
      />
      <Table
        rowKey="id"
        dataSource={filteredOrders}
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ x: 'max-content' }}
        expandable={{ expandedRowRender }}
        pagination={{ pageSize: 6 }}
        size="middle"
      />
    </div>
  );
};

export default OrderList;
